// Community Feed Page
// Reddit-style community feed with posts

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Post, SortOption } from '../../types/community';
import { CommunityService } from '../../services/Community/communityService';
import { PostCard } from '../../components/CommunityChatRooms/PostCard';

import { RefreshButton } from '../../components/CommunityChatRooms/RefreshButton';
import { useChatRoomTheme } from '../../contexts/ChatRoomThemeContext';
import {
  CommunityFeedContainer,
  CommunityHeader,
  CommunityInfo,
  CommunityIcon,
  CommunityDetails,
  CommunityName,
  CommunityDescription,
  CommunityStats,
  CommunityStatItem,
  FeedControls,
  PostsList,
  CreatePostButton,
  FeedEmptyState,
  FeedEmptyStateIcon,
  FeedEmptyStateTitle,
  FeedEmptyStateText,
  LoadingState,
  CommunityFeedBackButton,
  ChatRoomToast,
} from '../../styles/CommunityChatRooms/CommunityFeed.styles';
import { CommunityInfo as CommunityInfoType } from '../../types/community';
import { signalsService } from '../../services/SocialFeed/signalsService';
import { ProfilePeekDrawer, useProfilePeek } from '../../components/shared/ProfilePeekDrawer';

export const CommunityFeed: React.FC = () => {
  const { personaId, roomId } = useParams<{ personaId?: string; roomId?: string }>();
  const navigate = useNavigate();
  const { theme } = useChatRoomTheme();
  // Same drawer the feeds use, so a name behaves identically wherever it
  // appears. 'communities' is the signal surface, which keeps profile_open and
  // profile_dwell from a chat room distinguishable from the same action taken
  // in Matches.
  const { peekTarget, openPeek, closePeek, toast, showToast } = useProfilePeek('communities');
  const communityId = personaId || roomId || '';
  const communityType = personaId ? 'persona' : 'custom';

  const [posts, setPosts] = useState<Post[]>([]);
  const [communityInfo, setCommunityInfo] = useState<CommunityInfoType | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>('new');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Scroll to top when entering the chat room
    window.scrollTo(0, 0);
    loadCommunityInfo();
    loadPosts();
  }, [communityId, communityType]);

  // Also scroll to top on initial mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadPosts();
  }, [sortOption]);

  const loadCommunityInfo = async () => {
    try {
      const info = await CommunityService.getCommunityInfo(communityId, communityType);
      setCommunityInfo(info);
    } catch (error) {
      console.error('Error loading community info:', error);
    }
  };

  const loadPosts = async () => {
    try {
      if (isLoading) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      const fetchedPosts = await CommunityService.getCommunityPosts(communityId, sortOption);
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleSortChange = (sort: SortOption) => {
    setSortOption(sort);
  };

  const handleRefresh = () => {
    loadPosts();
  };

  const handleBack = () => {
    navigate('/community-chat-rooms');
  };

  const handleCreatePost = () => {
    if (personaId) {
      navigate(`/community-chat-rooms/${personaId}/create`);
    } else if (roomId) {
      navigate(`/community-chat-rooms/room/${roomId}/create`);
    }
  };

  const handlePostClick = (postId: string) => {
    if (personaId) {
      navigate(`/community-chat-rooms/${personaId}/post/${postId}`);
    } else if (roomId) {
      navigate(`/community-chat-rooms/room/${roomId}/post/${postId}`);
    }
  };

  const handleVote = async (postId: string, voteType: 'upvote' | 'downvote') => {
    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      const wasUpvoted = post.userVote === 'upvote';
      const wasDownvoted = post.userVote === 'downvote';
      const isUpvoting = voteType === 'upvote';

      // Optimistically update UI
      setPosts((prevPosts) =>
        prevPosts.map((post) => {
          if (post.id === postId) {
            let newVote: 'upvote' | 'downvote' | null = null;
            let upvotes = post.upvotes;
            let downvotes = post.downvotes;

            if (isUpvoting) {
              if (wasUpvoted) {
                // Remove upvote
                newVote = null;
                upvotes -= 1;
              } else {
                // Add upvote
                newVote = 'upvote';
                upvotes += 1;
                if (wasDownvoted) {
                  downvotes -= 1;
                }
              }
            } else {
              if (wasDownvoted) {
                // Remove downvote
                newVote = null;
                downvotes -= 1;
              } else {
                // Add downvote
                newVote = 'downvote';
                downvotes += 1;
                if (wasUpvoted) {
                  upvotes -= 1;
                }
              }
            }

            return {
              ...post,
              userVote: newVote,
              upvotes,
              downvotes,
            };
          }
          return post;
        })
      );

      // Call service
      await CommunityService.voteOnPost(postId, voteType);
      // The target is the post's AUTHOR: the lake records who is interested in
      // whom, and a post id alone would be unusable for matching.
      const votedPost = posts.find((p) => p.id === postId);
      if (votedPost?.authorId) {
        signalsService.track({
          signalType: voteType === 'upvote' ? 'post_upvote' : 'post_downvote',
          targetUserId: votedPost.authorId,
          surface: 'communities',
          itemId: postId,
        });
      }
    } catch (error) {
      console.error('Error voting on post:', error);
      // Revert optimistic update on error
      loadPosts();
    }
  };

  const handleSave = async (postId: string) => {
    try {
      await CommunityService.savePost(postId);
      const savedPost = posts.find((p) => p.id === postId);
      if (savedPost?.authorId) {
        signalsService.track({
          signalType: 'post_save',
          targetUserId: savedPost.authorId,
          surface: 'communities',
          itemId: postId,
        });
      }
      // Update local state
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId ? { ...post, isSaved: !post.isSaved } : post
        )
      );
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  if (isLoading) {
    return (
      <CommunityFeedContainer $theme={theme}>
        <LoadingState $theme={theme}>Loading community...</LoadingState>
      </CommunityFeedContainer>
    );
  }

  if (!communityInfo) {
    return (
      <CommunityFeedContainer $theme={theme}>
        <FeedEmptyState $theme={theme}>
          <FeedEmptyStateTitle $theme={theme}>Community not found</FeedEmptyStateTitle>
        </FeedEmptyState>
      </CommunityFeedContainer>
    );
  }

  return (
    <CommunityFeedContainer $theme={theme}>
      <div style={{ padding: '0 1.25rem', paddingTop: '1rem', marginBottom: '0.5rem' }}>
        <CommunityFeedBackButton $theme={theme} onClick={handleBack}>
          ← Back to Chat Rooms
        </CommunityFeedBackButton>
      </div>
      <CommunityHeader $theme={theme}>
        <CommunityInfo>
          <CommunityIcon $theme={theme}>{communityInfo.icon}</CommunityIcon>
          <CommunityDetails>
            <CommunityName $theme={theme}>{communityInfo.name}</CommunityName>
            <CommunityDescription $theme={theme}>{communityInfo.description}</CommunityDescription>
          </CommunityDetails>
        </CommunityInfo>
        <CommunityStats $theme={theme}>
          <CommunityStatItem>
            👥 {communityInfo.memberCount.toLocaleString()} members
          </CommunityStatItem>
        </CommunityStats>
      </CommunityHeader>

      <FeedControls>

        <RefreshButton
          onClick={handleRefresh}
          disabled={isRefreshing}
          isRefreshing={isRefreshing}
        />
      </FeedControls>

      <PostsList>
        {posts.length > 0 ? (
          posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onClick={() => handlePostClick(post.id)}
              onVote={handleVote}
              onSave={handleSave}
              onAuthorClick={(authorId, authorName) =>
                openPeek({ profileId: authorId, firstName: authorName, context: 'From this chat room' })}
            />
          ))
        ) : (
          <FeedEmptyState $theme={theme}>
            <FeedEmptyStateIcon>📝</FeedEmptyStateIcon>
            <FeedEmptyStateTitle $theme={theme}>No posts yet</FeedEmptyStateTitle>
            <FeedEmptyStateText>
              Be the first to start a discussion in this community!
            </FeedEmptyStateText>
          </FeedEmptyState>
        )}
      </PostsList>

      <CreatePostButton onClick={handleCreatePost} aria-label="Create post">
        +
      </CreatePostButton>

      {/* Slides in over the room rather than navigating away — the point is to
          glance at someone without losing the conversation. */}
      <ProfilePeekDrawer
        target={peekTarget}
        onClose={closePeek}
        backLabel="Chat Room"
        onNotify={(_type, message) => showToast(message)}
      />
      {toast && <ChatRoomToast $theme={theme}>{toast}</ChatRoomToast>}
    </CommunityFeedContainer>
  );
};

